import React from "react"
import {
  FieldErrors,
  Path,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form"
import { RadioGroup, Radio } from "@headlessui/react"
import { useTranslation } from "react-i18next"

import { getLocalizedText } from "utils/i18n/get-localized-text"
import { PlaceOrderFormData, Question } from "types"

interface RadioGroupQuestionProps {
  question: Question
  errors: FieldErrors<PlaceOrderFormData>
  watch: UseFormWatch<PlaceOrderFormData>
  setValue: UseFormSetValue<PlaceOrderFormData>
  ticketId: number
}

const RadioGroupQuestion: React.FC<RadioGroupQuestionProps> = ({
  question,
  errors,
  watch,
  setValue,
  ticketId,
}) => {
  const { t } = useTranslation()
  const fieldName =
    `tickets.${ticketId}.${question.uuid}` as Path<PlaceOrderFormData>
  const watchedValue = watch(fieldName)

  const questionLabel = getLocalizedText(question.text, "Question")

  if (!question.choices || question.choices.length === 0) {
    return (
      <div className="text-red-500">
        {t("validation.no_choices_available", "Error: No choices available for this question")}
      </div>
    )
  }

  const sortedChoices = [...question.choices].sort((a, b) => a.rank - b.rank)

  const handleRadioChange = (choiceId: number | null) => {
    const value = choiceId ? [choiceId] : []
    setValue(fieldName, value as any)
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {questionLabel}
        {question.requires_answer && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      <RadioGroup
        value={
          Array.isArray(watchedValue) && watchedValue.length > 0
            ? watchedValue[0]
            : null
        }
        onChange={handleRadioChange}
        className="space-y-2"
        name={fieldName}
      >
        <div className="space-y-3">
          {sortedChoices.map((choice) => (
            <Radio
              key={choice.id}
              value={choice.id}
              as="div"
              className={({ checked }) =>
                `relative flex items-center cursor-pointer ${
                  checked ? "text-theme_text" : "text-theme_text"
                }`
              }
            >
              {({ checked }) => (
                <>
                  <div className="flex relative items-center">
                    <div
                      className={`relative shrink-0 w-[20px] h-[20px] rounded-full border-[2px] ${
                        checked
                          ? "border-theme_accent bg-theme_accent"
                          : "bg-white border-input_border"
                      }`}
                    >
                      {checked && (
                        <div className="absolute top-1/2 left-1/2 h-[10px] w-[10px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"></div>
                      )}
                    </div>
                  </div>
                  <span className="ml-3 text-sm text-theme_text">
                    {getLocalizedText(choice.text, "Choice")}
                  </span>
                </>
              )}
            </Radio>
          ))}
        </div>
      </RadioGroup>

      {(() => {
        const fieldError = errors.tickets?.[ticketId]?.[question.uuid]
        return (
          fieldError && (
            <span className="text-xs text-red-500">{fieldError.message}</span>
          )
        )
      })()}
    </div>
  )
}

export { RadioGroupQuestion }
